import uniqWith from 'lodash/uniqWith';
import {CardPaymentTypes, PaymentTypes} from 'new-constants/common.constants';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import isBoolean from 'lodash/isBoolean';
import isArray from 'lodash/isArray';
import {isRestaurantDelivery, isRestaurantDineIn, OrderingTypeFilters} from "./restaurant.helper";
import {OrderingTypes} from "../constants/common.constants";

function getRestaurantFromCache(restaurantId, cache) {
	if (!restaurantId) {
		return null;
	}
	
	const restaurant = cache.find(restaurant => {
		return restaurant.restaurantId === restaurantId;
	});
	
	if (!restaurant) {
		return null;
	}
	
	return restaurant;
}

function getMergedOrders(oldOrders, newOrders) {
	return uniqWith(newOrders.concat(oldOrders), (firstOrder, secondOrder) => {
		return firstOrder.orderId === secondOrder.orderId;
	});
}

function isOrderValid(order) {
	return !!(
		isNumber(order.amount) &&
		isString(order.deliveryPrice) &&
		isString(order.deliveryTo) &&
		isBoolean(order.isCanceled) &&
		isBoolean(order.isConfirmed) &&
		isBoolean(order.isDelivered) &&
		isBoolean(order.isPaid) &&
		isBoolean(order.isUserCancellable) &&
		isBoolean(order.isExpressKiosk) &&
		isBoolean(order.oneTime) &&
		isNumber(order.orderId) &&
		isArray(order.orderItems) &&
		//TODO uncomment when dinein order sends payment type
		//isNumber(order.paymentType) &&
		isString(order.pickupAt) &&
		isNumber(order.pickupPlaceId) &&
		isString(order.pickupRef) &&
		isString(order.pickupTime) &&
		isString(order.qrCodeUrl) &&
		isNumber(order.restaurantId) &&
		isString(order.restaurantName) &&
		isBoolean(order.showWaiterId) &&
		isString(order.stamp) &&
		isString(order.statusString) &&
		isString(order.statusTime) &&
		isString(order.statusTimeNew) &&
		isString(order.tip) &&
		isString(order.total) &&
		isString(order.type) &&
		isString(order.validUntil) &&
		isString(order.vat) &&
		isString(order.deliveryTime) &&
		isString(order.deliveredBy) &&
		isString(order.deliveryTimeNew) &&
		isBoolean(order.isAsap)
	);
}

function getValidOrders(orders) {
	if (!orders) {
		return [];
	}
	
	return orders.filter(order => isOrderValid(order));
}

function getCacheWithMergedUpdatedOrders(updatedOrders, restaurantId, cache) {
	const restaurant = getRestaurantFromCache(restaurantId, cache) || {
		restaurantId,
		orders: [],
	};
	
	return [
		...cache.filter(restaurantOrders => restaurantOrders.restaurantId !== restaurantId),
		{
			restaurantId: restaurantId,
			orders: getMergedOrders(restaurant.orders, updatedOrders),
		},
	];
}

export const getOrderFromCache = (orderIdToFind, cache) => {
	if (!orderIdToFind || !cache) {
		return null;
	}
	
	return cache.reduce((accum, restaurantCache) => {
		return accum || restaurantCache.orders.find(order => order.orderId.toString() === orderIdToFind.toString());
	}, null);
};

export const getCacheWithMergedUpdatedOrdersByRestaurants = (updatedOrdersByRestaurants, cache) => {
	if (!updatedOrdersByRestaurants) {
		return cache;
	}
	
	let newCache = cache;
	
	updatedOrdersByRestaurants.forEach(updatedRestaurantOrders => {
		newCache = getCacheWithMergedUpdatedOrders(
			getValidOrders(updatedRestaurantOrders.orders),
			updatedRestaurantOrders.restaurantId,
			newCache
		);
	});
	
	return newCache;
};

export const getOrderWithVatTotalByPercent = order => {
	if (!(order && order.orderItems)) {
		return null;
	}
	
	const vatTotalsByPercent = {};
	
	order.orderItems.forEach(orderItem => {
		if (vatTotalsByPercent[orderItem.vatPercent]) {
			vatTotalsByPercent[orderItem.vatPercent] += orderItem.vatSum;
		} else {
			vatTotalsByPercent[orderItem.vatPercent] = orderItem.vatSum;
		}
	});
	
	return {
		...order,
		vatTotalsByPercent,
	};
};

export const isActiveOrder = order => {
	return (
		order.isCanceled ? false :
			((order.isDelivered === false &&
				order.statusString !== 'Purchase Not Finished' &&
				!(CardPaymentTypes.includes(order.paymentType) && order.isPaid === false) &&
				!(order.paymentType === PaymentTypes.payWithVipps && order.isPaid === false) &&
				!(order.paymentType === PaymentTypes.payWithMobilePay && order.isPaid === false)))
	
	);
};

export const isHistoryOrder = order => {
	return order.isCanceled === true || order.isDelivered === true;
};

export const getOrderingTypeFromOrder = (order, orderRestaurant) => {
	if (!order || !orderRestaurant) {
		return null;
	}
	
	//TODO ask backend to return ordering type
	if (!order.deliveryTo) {
		return OrderingTypes.pickup;
	}
	
	if (isRestaurantDineIn(orderRestaurant)) {
		return OrderingTypes.dineIn;
	}
	
	if (isRestaurantDelivery(orderRestaurant)) {
		return OrderingTypes.delivery;
	}
	
	return null;
}

export const getItemStringFromOrder = (order) => {
	if (!order) {
		return null;
	}
	
	return order.orderItems.map((item) => item.menuEntry).join(`, `)
}
